import React, { FC, useEffect, useState } from "react";
import { Step, Stepper } from "@life-line-lab/components-react";
import { find, omitBy, isEmpty } from "lodash";
import useCountriesList from "Hooks/countries-option.hook";
import useStatesList from "Hooks/states-option.hook";
import ParcelOrderInfo from "./stepperContents/parcel-order-info/parcel-order-info";
import RecipientInfo from "./stepperContents/parcel-order-info/recipient-info";
import { generateSteps } from "Helpers/generate-stepper-steps";
import DeliveryInfo from "./stepperContents/parcel-order-info/delivery-info";
import ParcelInfo from "./stepperContents/parcel-order-info/parcel-info";
import ConfirmOrderInfo from "./stepperContents/parcel-order-info/order-check";
import useOrderFormStates from "./order-form-states";

const initialSteps: Step[] = generateSteps<Step>(5);
const arr = Array(5).fill(0);

const OrderCreateForm: FC<any> = (props) => {
  const {
    onSubmit,
    onSubmitCalculate,
    companyDetail,
    customerDetails,
    isRoleCustomer,
    handleFetchCustomerList,
    onFetchDeliveryServiceList,
    onFetchCustomerLocationList,
    handleCoordinatesChecking,
    handleFetchEstimatedDeliveryDays,
    isEdit,
    isSplitShipmentEdit = false
  } = props || {};

  const {
    goodCareConditionList,
    locationList,
    doesPostcodeExist,
    deliveryServiceTypes,
    customerList,
    orderEditDetail,
    isLoading,
    estimatedOrder,
  } = useOrderFormStates();

  const [input, setInput] = useState<any>({});
  const [steps, setSteps] = useState<any>(initialSteps);
  const [current, setCurrent] = useState<number>(0);

  // COUNTRIES, STATES OPTIONS
  const stateOption = useStatesList(input?.country);
  const countryOption = useCountriesList();

  const prev = (childInput?) => {
    const prevStep = find(steps, (_, index) => {
      return index === (current - 1).toString();
    });

    if (prevStep) {
      prevStep!.status = "process";
      steps.splice(current - 1, 1, prevStep);
      setSteps(steps);
    }

    if (current !== 0) {
      const reduce = current - 1;
      setCurrent(reduce);
    }

    if (childInput) {
      setInput({ ...input, ...childInput });
    }
  };

  const next = async (childInput) => {
    const currentStep = find(steps, (_, index) => {
      return index === current.toString();
    });

    //check postcode from the recipient name
    if (childInput.recipientName && childInput.postcode) {
      let hasDeliveryList = await onFetchDeliveryServiceList(
        input?.customer,
        childInput.postcode,
      );
      if (!hasDeliveryList) {
        return;
      }
    }

    if (current !== arr.length - 1) {
      if (currentStep) {
        currentStep!.status = "success";
        steps.splice(current, 1, currentStep);
        setSteps(steps);
      }
      const add = current + 1;
      setCurrent(add);
    }

    setInput({ ...input, ...childInput });
  };

  const handleSubmit = (childInput) => {
    let orderObj = omitBy({ ...input, ...childInput }, isEmpty);

    // for any boolean, add it here, in order-create, order edit at handleCalculateOrder and model
    orderObj = {
      ...orderObj,
      hasInsurance: childInput.hasInsurance,
      looseOrders: childInput.looseOrders,
    };

    setInput(orderObj);
    onSubmitCalculate(orderObj); // Whole Order Object to check price (all 4 steps)
  };

  const handleSubmitFinal = (finalInput) => {
    setInput(finalInput);

    if (input.isEdit) {
      // check if finalInput has order
      const { order = {} } = finalInput || {};

      onSubmit({ ...order, ...input });
      return;
    }
    onSubmit(finalInput);
  };

  useEffect(() => {
    if (!isEmpty(orderEditDetail)) {
      setInput(orderEditDetail);
    }
  }, [orderEditDetail]);

  useEffect(() => {
    setSteps(generateSteps(filteredStepperContents().length))
  }, [isSplitShipmentEdit])

  // to control steps to display based on any condition
  const filteredStepperContents = () => {
    let allSteps = [
      <ParcelOrderInfo
        onNext={next}
        companyDetail={companyDetail}
        customerDetails={customerDetails}
        isRoleCustomer={isRoleCustomer}
        customerList={customerList}
        locationList={locationList}
        countryOption={countryOption}
        handleFetchCustomerList={handleFetchCustomerList}
        handleFetchEstimatedDeliveryDays={handleFetchEstimatedDeliveryDays}
        fetchCustomerLocation={onFetchCustomerLocationList}
        parentInput={input}
        isEdit={isEdit}
      />,
      <RecipientInfo
        doesPostcodeExist={doesPostcodeExist}
        onPrev={prev}
        onNext={next}
        countryOption={countryOption}
        parentInput={input}
        handleCoordinatesChecking={handleCoordinatesChecking}
      />,
      <DeliveryInfo
        orderEditDetail={orderEditDetail}
        onPrev={prev}
        onNext={next}
        deliveryServiceTypes={deliveryServiceTypes}
        parentInput={input}
        goodCareConditionList={goodCareConditionList}
      />,
      <ParcelInfo
        onPrev={prev}
        onNext={next}
        handleSubmit={handleSubmit}
        parentInput={input}
        goodCareConditionList={goodCareConditionList}
        isEdit={isEdit}
        current={current}
      />,
      <ConfirmOrderInfo
        info={estimatedOrder}
        deliveryServiceTypes={deliveryServiceTypes}
        onPrev={prev}
        handleSubmit={handleSubmitFinal}
        parentInput={input}
        isLoading={isLoading}
      />,
    ];

    if (isSplitShipmentEdit) allSteps.splice(0, 3);

    return allSteps;
  };

  return (
    <div className="order-create-form">
      <form>
        <React.Fragment>
          <Stepper activeKey={current} steps={steps}>
            {filteredStepperContents()[current]}
          </Stepper>
        </React.Fragment>
      </form>
    </div>
  );
};

export default OrderCreateForm;
